import React, { useEffect, useState, useCallback } from 'react';
import { Lightbulb, CheckCircle2, BarChart3, AlertTriangle, Pin, Image, ClipboardList, PartyPopper, FileText, Trophy, PlayCircle, Presentation, Check, Circle, Compass } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import PresentationPlayer from './presentation/PresentationPlayer';
import PreReadingLetterhead from './PreReadingLetterhead';
import { LESSON_TOOL_TYPES, ReviewList } from './LessonTools';
import CourseOverview, { CourseProgress } from './CourseOverview';
import { categoryColour, withAlpha } from '../data/categoryColours';
import './LessonPlayer.css';


function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, j) =>
    p.startsWith('**') ? <strong key={j}>{p.slice(2, -2)}</strong> : p
  );
}

function renderContent(text) {
  if (!text) return null;
  return text.split('\n\n').filter(p => p.trim()).map((para, i) => {
    const lines = para.split('\n');
    const hasListItems = lines.some(l => l.trimStart().startsWith('- '));

    if (hasListItems) {
      const nodes = [];
      let listItems = [];
      lines.forEach((line, j) => {
        if (line.trimStart().startsWith('- ')) {
          listItems.push(line.trimStart().slice(2));
        } else {
          if (listItems.length > 0) {
            nodes.push(
              <ul key={`ul-${j}`}>
                {listItems.map((item, k) => <li key={k}>{renderInline(item)}</li>)}
              </ul>
            );
            listItems = [];
          }
          if (line.trim()) nodes.push(<p key={`p-${j}`}>{renderInline(line)}</p>);
        }
      });
      if (listItems.length > 0) {
        nodes.push(
          <ul key="ul-end">
            {listItems.map((item, k) => <li key={k}>{renderInline(item)}</li>)}
          </ul>
        );
      }
      return <div key={i}>{nodes}</div>;
    }

    if (lines.length === 1) return <p key={i}>{renderInline(para)}</p>;

    return (
      <p key={i}>
        {lines.map((line, j) => (
          <React.Fragment key={j}>
            {renderInline(line)}
            {j < lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </p>
    );
  });
}

/* ─── FlipCard ─── */
function FlipCard({ front, back, color }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <div className={`flip-card ${flipped ? 'flip-card--flipped' : ''}`} onClick={() => setFlipped(f => !f)}>
      <div className="flip-card-inner">
        <div className="flip-card-front" style={{ borderTopColor: color || 'var(--primary)' }}>
          <span className="flip-card-icon"><Lightbulb size={20} strokeWidth={1.75} /></span>
          <p className="flip-card-front-text">{front}</p>
          <span className="flip-card-hint">Tap to see answer</span>
        </div>
        <div className="flip-card-back" style={{ background: color ? withAlpha(color, 8) : 'var(--primary-light)', borderTopColor: color || 'var(--primary)' }}>
          <span className="flip-card-icon"><CheckCircle2 size={20} strokeWidth={1.75} /></span>
          <p className="flip-card-back-text">{back}</p>
          <span className="flip-card-hint">Tap to flip back</span>
        </div>
      </div>
    </div>
  );
}

function FlashcardDeck({ section, color }) {
  const cards = section.cards || [];
  return (
    <div className="flashcard-section">
      <div className="section-label-row">
        <span className="section-label-icon">🃏</span>
        <span className="section-label-title">{section.title}</span>
        <span className="section-label-badge">{cards.length} cards · tap to flip</span>
      </div>
      <div className="flashcard-grid">
        {cards.map((card, i) => (
          <FlipCard key={i} front={card.front} back={card.back} color={color} />
        ))}
      </div>
    </div>
  );
}

/* ─── ComparisonTable ─── */
function ComparisonTable({ section }) {
  const { title, headers = [], rows = [] } = section;
  return (
    <div className="comparison-section">
      <div className="section-label-row">
        <span className="section-label-icon"><BarChart3 size={18} strokeWidth={1.75} /></span>
        <span className="section-label-title">{title}</span>
      </div>
      <div className="comparison-table-wrap">
        <table className="comparison-table">
          <thead>
            <tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci}>{ci === 0 ? <strong>{cell}</strong> : cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── TipBlock ─── */
function TipBlock({ section }) {
  const typeMap = {
    warning: { icon: <AlertTriangle size={18} strokeWidth={1.75} />, label: 'Watch Out', cls: 'tip-warning' },
    note:    { icon: <Pin size={18} strokeWidth={1.75} />,           label: 'Note',       cls: 'tip-note' },
    tip:     { icon: <Lightbulb size={18} strokeWidth={1.75} />,     label: 'Pro Tip',    cls: 'tip-tip' },
    success: { icon: <CheckCircle2 size={18} strokeWidth={1.75} />,  label: 'Key Point',  cls: 'tip-success' },
  };
  const t = typeMap[section.tipType || 'tip'];
  return (
    <div className={`tip-block ${t.cls}`}>
      <span className="tip-icon">{t.icon}</span>
      <div>
        <strong className="tip-label">{t.label}</strong>
        <p className="tip-content">{section.content}</p>
      </div>
    </div>
  );
}

/* ─── DiagramBlock ─── */
function DiagramBlock({ section }) {
  return (
    <div className="diagram-section">
      <div className="section-label-row">
        <span className="section-label-icon"><Image size={18} strokeWidth={1.75} /></span>
        <span className="section-label-title">{section.title}</span>
      </div>
      <div className="diagram-wrap">
        <div className="diagram-svg-container" dangerouslySetInnerHTML={{ __html: section.svg }} />
        {section.caption && <p className="diagram-caption">{section.caption}</p>}
      </div>
    </div>
  );
}

/* ─── StepBlock ─── */
function StepBlock({ section }) {
  const steps = section.steps || [];
  return (
    <div className="step-section">
      <div className="section-label-row">
        <span className="section-label-icon"><ClipboardList size={18} strokeWidth={1.75} /></span>
        <span className="section-label-title">{section.title}</span>
      </div>
      <ol className="step-list">
        {steps.map((step, i) => (
          <li key={i} className="step-item">
            <span className="step-num">{i + 1}</span>
            <div className="step-content">
              {(step.heading || step.title) && <strong className="step-heading">{step.heading || step.title}</strong>}
              <span className="step-desc">{step.desc || step.description}</span>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ─── AccordionSection ─── */
function AccordionSection({ section }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`acc-section ${open ? 'acc-section--open' : ''}`}>
      <button className="acc-header" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <span className="acc-title">{section.title}</span>
        <span className="acc-chevron" aria-hidden="true">{open ? '▲' : '▼'}</span>
      </button>
      {open && <div className="acc-body">{renderContent(section.content)}</div>}
    </div>
  );
}

function ImageBlock({ section }) {
  return (
    <figure className="lesson-image-block">
      <img src={section.url} alt={section.caption || section.title || 'Course illustration'} className="lesson-image" loading="lazy" />
      {section.caption && <figcaption className="lesson-image-caption">{section.caption}</figcaption>}
      <a className="lesson-image-expand" href={section.url} target="_blank" rel="noopener noreferrer">View full-size image ↗</a>
    </figure>
  );
}

/* ─── Section dispatcher ─── */
function RenderSection({ section, color, ctx }) {
  const Tool = LESSON_TOOL_TYPES[section.type];
  if (Tool) return <Tool section={section} ctx={ctx} />;
  switch (section.type) {
    case 'flashcard': return <FlashcardDeck section={section} color={color} />;
    case 'table':     return <ComparisonTable section={section} />;
    case 'tip':       return <TipBlock section={section} />;
    case 'diagram':   return <DiagramBlock section={section} />;
    case 'steps':     return <StepBlock section={section} />;
    case 'image':     return <ImageBlock section={section} />;
    default:          return <AccordionSection section={section} />;
  }
}

function LessonObjectives({ items }) {
  return (
    <div className="lesson-objectives">
      <p className="lesson-objectives-label">Lesson Objectives</p>
      <ul className="lesson-objectives-list">
        {items.map((item, i) => <li key={i}>{item}</li>)}
      </ul>
    </div>
  );
}

function ModuleGuide({ module }) {
  if (!module || (!module.objective && !module.activity && !module.takeaway && !module.transition)) return null;

  const items = [
    { key: 'objective', label: 'Learning objective', icon: <Trophy size={17} strokeWidth={1.75} />, text: module.objective },
    { key: 'activity', label: 'Practical activity', icon: <ClipboardList size={17} strokeWidth={1.75} />, text: module.activity },
    { key: 'takeaway', label: 'Key takeaway', icon: <Lightbulb size={17} strokeWidth={1.75} />, text: module.takeaway },
    { key: 'transition', label: 'What comes next', icon: <CheckCircle2 size={17} strokeWidth={1.75} />, text: module.transition },
  ].filter(item => item.text);

  return (
    <section className="module-guide" aria-label={`${module.title} learning guide`}>
      {items.map(item => (
        <div className={`module-guide-item module-guide-item--${item.key}`} key={item.key}>
          <span className="module-guide-icon">{item.icon}</span>
          <div>
            <p className="module-guide-label">{item.label}</p>
            <p className="module-guide-text">{item.text}</p>
          </div>
        </div>
      ))}
    </section>
  );
}

// Order questions per attempt so learners reason about each one rather than
// memorising positions. Seeded by attempt so a re-render keeps the order.
function orderQuestions(questions, attempt) {
  const arr = questions.map((q, i) => ({ q, k: Math.sin((i + 1) * 9301 + attempt * 49297) }));
  return attempt === 0 ? questions : arr.sort((a, b) => a.k - b.k).map(x => x.q);
}

// Draft answers are kept on this device so a refresh or a dropped connection
// does not lose a half-finished quiz.
const draftKey = (userId, quizId) => `okiru_quiz_draft:${userId}:${quizId}`;
function loadDraft(userId, quizId) {
  try { return JSON.parse(localStorage.getItem(draftKey(userId, quizId)) || '{}') || {}; } catch { return {}; }
}
function saveDraft(userId, quizId, answers) {
  try { localStorage.setItem(draftKey(userId, quizId), JSON.stringify(answers)); } catch { /* storage unavailable */ }
}
function clearDraft(userId, quizId) {
  try { localStorage.removeItem(draftKey(userId, quizId)); } catch { /* storage unavailable */ }
}

function Quiz({ userId, courseId, quiz, quizScores, submitQuiz, onDone, isFinal }) {
  const passMark  = quiz.passingScore ?? 75;
  const [answers,   setAnswersState] = useState(() => (quizScores[quiz.id] ? {} : loadDraft(userId, quiz.id)));
  const [submitted, setSubmitted] = useState(!!quizScores[quiz.id]);
  const [result,    setResult]    = useState(quizScores[quiz.id] || null);
  const [attempt,   setAttempt]   = useState(quizScores[quiz.id] ? 1 : 0);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');
  const questions = orderQuestions(quiz.questions, attempt);

  const setAnswers = (updater) => {
    setAnswersState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      saveDraft(userId, quiz.id, next);
      return next;
    });
  };

  // The server grades the answers and returns the result.
  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await submitQuiz(courseId, quiz.id, answers);
      clearDraft(userId, quiz.id);
      setResult(res);
      setSubmitted(true);
    } catch (err) {
      setError(`${err.message} Your answers are kept, so you can try again.`);
    } finally {
      setSaving(false);
    }
  };

  const allAnswered = quiz.questions.every(q => q.type === 'numeric' ? String(answers[q.id] ?? '').trim() !== '' : answers[q.id] !== undefined);

  if (submitted && result) {
    const review = (result.wrongs || []).filter(w => w.correctAnswer !== undefined);
    return (
      <div className="quiz-result">
        <div className={`quiz-result-icon ${result.passed ? 'quiz-result-icon--pass' : 'quiz-result-icon--fail'}`}>
          {result.passed ? <PartyPopper size={32} strokeWidth={1.75} /> : <FileText size={32} strokeWidth={1.75} />}
        </div>
        <h3 className={result.passed ? 'quiz-pass' : 'quiz-fail'}>{result.passed ? 'Passed!' : 'Not quite'}</h3>
        <p className="quiz-score">{result.correct}/{result.total} correct | {result.score}%</p>
        <p className="quiz-msg">
          {result.passed
            ? (isFinal ? 'Congratulations! You have passed the final assessment and earned your certificate.' : 'Great work. This module is complete.')
            : `You need ${passMark}% to pass. Review the explanations below, revisit the lessons and try again.`}
        </p>
        <ReviewList items={review} />
        {result.passed && <button className="btn btn-primary" onClick={onDone}>{isFinal ? <><Trophy size={16} strokeWidth={1.75} /> View Certificate</> : 'Continue'}</button>}
        {!result.passed && <button className="btn btn-ghost" onClick={() => { setSubmitted(false); setAnswersState({}); clearDraft(userId, quiz.id); setResult(null); setAttempt(a => a + 1); }}>Retry {isFinal ? 'Assessment' : 'Quiz'}</button>}
      </div>
    );
  }

  return (
    <div className="quiz">
      <h3 className="quiz-title">{quiz.title}</h3>
      <p className="quiz-subtitle">{isFinal ? `${quiz.questions.length} questions. Score ${passMark}% or higher to earn your certificate.` : `Answer all ${quiz.questions.length} questions. ${passMark}% required to pass.`}</p>
      <div className="quiz-questions">
        {questions.map((q, qi) => (
          <div key={q.id} className="quiz-q">
            <p className="quiz-q-text"><span className="quiz-q-num">Q{qi+1}.</span> {q.question}</p>
            {q.type === 'numeric' ? (
              <div className="quiz-numeric">
                <label className="lt-sr" htmlFor={`quiz-${quiz.id}-${q.id}`}>Your answer</label>
                {q.unit === 'R' && <span className="lt-affix">R</span>}
                <input id={`quiz-${quiz.id}-${q.id}`} inputMode="decimal" placeholder="Enter a number" value={answers[q.id] ?? ''} onChange={e => setAnswers(prev => ({ ...prev, [q.id]: e.target.value }))} />
                {q.unit && q.unit !== 'R' && <span className="lt-affix">{q.unit}</span>}
              </div>
            ) : (
            <div className="quiz-options">
              {q.options.map((opt, oi) => (
                <button
                  key={oi}
                  className={`quiz-option ${answers[q.id] === oi ? 'quiz-option--selected' : ''}`}
                  aria-pressed={answers[q.id] === oi}
                  onClick={() => setAnswers(prev => ({ ...prev, [q.id]: oi }))}
                >
                  <span className="quiz-opt-letter">{String.fromCharCode(65+oi)}</span>
                  {opt}
                </button>
              ))}
            </div>
            )}
          </div>
        ))}
      </div>
      {error && <p className="quiz-error" role="alert">{error}</p>}
      <button className="btn btn-primary quiz-submit" disabled={!allAnswered || saving} onClick={submit}>
        {saving ? 'Submitting…' : 'Submit answers'}
      </button>
    </div>
  );
}

function SidebarRating({ courseId, userId }) {
  const [hovered,  setHovered]  = React.useState(0);
  const [selected, setSelected] = React.useState(0);
  const [saved,    setSaved]    = React.useState(false);

  async function submit(star) {
    setSelected(star);
    try {
      await fetch(`/api/courses/${courseId}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, rating: star }),
      });
      setSaved(true);
    } catch (_) {}
  }

  const display = hovered || selected;

  return (
    <div className="player-rating">
      <span className="player-rating-label">{saved ? 'Thanks for rating!' : 'Rate this course'}</span>
      <div className="player-rating-stars">
        {[1,2,3,4,5].map(i => (
          <button key={i} className="player-star-btn"
            style={{ color: i <= display ? 'var(--ok-finance-fill)' : 'var(--text-muted)' }}
            onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(0)}
            onClick={() => submit(i)} aria-label={`Rate ${i} stars`}>★</button>
        ))}
      </div>
    </div>
  );
}

export default function LessonPlayer({ user, courseId, moduleId, progress, quizScores, certificates, markLessonComplete, submitQuiz, issueCertificate, assignments, navigateTo }) {
  const [course,        setCourse]       = useState(null);
  const [activeModule,  setActiveModule] = useState(null);
  const [activeLesson,  setActiveLesson] = useState(null);
  const [showQuiz,      setShowQuiz]     = useState(false);
  const [showFinal,     setShowFinal]    = useState(false);
  const [loading,       setLoading]      = useState(true);
  const [showOverview,  setShowOverview] = useState(false);

  useEffect(() => {
    if (!courseId) return;
    fetch(`/api/courses/${courseId}?userId=${user?.id ?? ''}`)
      .then(r => r.json())
      .then(j => {
        setCourse(j.data);
        if (j.data.locked) { setLoading(false); return; }
        const mod = j.data.modules.find(m => m.id === moduleId) || j.data.modules[0];
        setActiveModule(mod);
        setActiveLesson(mod.lessons[0]);
        // Land on the course overview the first time a learner opens a course
        // (no module chosen and nothing completed yet).
        const started = j.data.modules.some(m => m.lessons.some(l => progress[l.id]));
        setShowOverview(!moduleId && !started);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [courseId, moduleId, user?.id]);

  const selectLesson = useCallback((mod, lesson) => {
    setActiveModule(mod);
    setActiveLesson(lesson);
    setShowQuiz(false);
    setShowFinal(false);
    setShowOverview(false);
  }, []);

  const allLessonsInModuleDone = useCallback((mod) => {
    return mod.lessons.every(l => progress[l.id]);
  }, [progress]);

  const markDone = () => {
    if (activeLesson) markLessonComplete(activeLesson.id);
  };

  const goNext = () => {
    if (!course || !activeModule || !activeLesson) return;
    const lessons = activeModule.lessons;
    const idx     = lessons.findIndex(l => l.id === activeLesson.id);
    if (idx < lessons.length - 1) { selectLesson(activeModule, lessons[idx + 1]); return; }
    if (allLessonsInModuleDone(activeModule) && activeModule.quiz && !quizScores[activeModule.quiz.id]?.passed) { setShowQuiz(true); return; }
    const mods = course.modules;
    const mIdx = mods.findIndex(m => m.id === activeModule.id);
    if (mIdx < mods.length - 1) { const nextMod = mods[mIdx+1]; selectLesson(nextMod, nextMod.lessons[0]); }
  };

  if (loading || !course) return <LoadingSpinner message="Loading lesson…" />;

  if (course.locked && course.lockReason === 'paid') {
    return (
      <div className="player-locked">
        <div className="player-locked-icon">🔒</div>
        <h2>This is a paid course</h2>
        <p>
          <strong>{course.title}</strong> is available once Okiru enrols you. Request access from the course catalogue and the team will be in touch.
        </p>
        <button className="btn btn-ghost" onClick={() => navigateTo('catalogue')}>← Back to the catalogue</button>
      </div>
    );
  }

  if (course.locked && course.lockReason === 'prerequisite') {
    return (
      <div className="player-locked">
        <div className="player-locked-icon">🔒</div>
        <h2>Finish the prerequisite first</h2>
        <p>
          <strong>{course.title}</strong> unlocks once you complete{' '}
          <strong>{course.prerequisiteCourseTitle || 'Claude for Professionals'}</strong>.
        </p>
        <button className="btn btn-ghost" onClick={() => navigateTo('portal')}>← Back to My Learning</button>
      </div>
    );
  }

  if (course.locked) {
    const unlockDate = new Date(course.availableFrom);
    return (
      <div className="player-locked">
        <div className="player-locked-icon">🔒</div>
        <h2>Not available yet</h2>
        <p>
          <strong>{course.title}</strong> unlocks on{' '}
          {unlockDate.toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long' })} at{' '}
          {unlockDate.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
          {course.schedule?.location ? ` at ${course.schedule.location}` : ''}.
        </p>
        <button className="btn btn-ghost" onClick={() => navigateTo('portal')}>← Back to My Learning</button>
      </div>
    );
  }

  const color       = categoryColour(course.category);
  const quizDone = (m) => !m.quiz || !!quizScores[m.quiz.id]?.passed;
  const allModsDone = course.modules.every(m => allLessonsInModuleDone(m) && quizDone(m));
  const hasCert     = certificates && certificates[courseId];
  const finalTest   = course.finalTest || null;
  const finalPassed = finalTest && quizScores[finalTest.id]?.passed;

  return (
    <div className="player">
      <div className="player-sidebar">
        <div className="player-course-title">
          <span className="player-cat" style={{ color, borderColor: color+'33', background: color+'11' }}>{course.category}</span>
          <p className="player-title">{course.title}</p>
        </div>
        <CourseProgress course={course} progress={progress} quizScores={quizScores} />
        <button
          className={`player-lesson player-overview-btn ${showOverview ? 'player-lesson--active' : ''}`}
          onClick={() => { setShowOverview(true); setShowQuiz(false); setShowFinal(false); }}
        >
          <span className="pl-check"><Compass size={14} strokeWidth={1.75} /></span>
          <span className="pl-title">Course overview</span>
        </button>
        <div className="player-modules">
          {course.modules.map(mod => {
            const modDone = allLessonsInModuleDone(mod);
            const quizPassed = allLessonsInModuleDone(mod) && quizDone(mod);
            return (
              <div key={mod.id} className={`player-mod ${activeModule?.id === mod.id ? 'player-mod--active' : ''}`}>
                <div className="player-mod-header" onClick={() => selectLesson(mod, mod.lessons[0])}>
                  <span className="player-mod-check">{quizPassed ? <Check size={14} strokeWidth={1.75} /> : modDone ? '◑' : <Circle size={14} strokeWidth={1.75} />}</span>
                  <span className="player-mod-name">{mod.title}</span>
                </div>
                {activeModule?.id === mod.id && (
                  <div className="player-lessons">
                    {mod.lessons.map(lesson => (
                      <button
                        key={lesson.id}
                        className={`player-lesson ${activeLesson?.id === lesson.id && !showQuiz ? 'player-lesson--active' : ''}`}
                        onClick={() => selectLesson(mod, lesson)}
                      >
                        <span className="pl-check">{progress[lesson.id] ? <Check size={14} strokeWidth={1.75} /> : <Circle size={14} strokeWidth={1.75} />}</span>
                        <span className="pl-title">{lesson.title}</span>
                        <span className="pl-meta">{lesson.type === 'video' ? <PlayCircle size={14} strokeWidth={1.75} /> : lesson.type === 'presentation' ? <Presentation size={14} strokeWidth={1.75} /> : <FileText size={14} strokeWidth={1.75} />} {lesson.duration}</span>
                      </button>
                    ))}
                    {modDone && mod.quiz && (
                      <button
                        className={`player-lesson player-lesson--quiz ${showQuiz ? 'player-lesson--active' : ''}`}
                        onClick={() => setShowQuiz(true)}
                      >
                        <span className="pl-check">{quizScores[mod.quiz?.id]?.passed ? <Check size={14} strokeWidth={1.75} /> : <Circle size={14} strokeWidth={1.75} />}</span>
                        <span className="pl-title">{mod.quiz?.title}</span>
                        <span className="pl-meta"><FileText size={14} strokeWidth={1.75} /> Quiz</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {allModsDone && finalTest && (
          <div className="player-final-wrap">
            <button
              className={`player-lesson player-lesson--quiz ${showFinal ? 'player-lesson--active' : ''}`}
              style={{ paddingLeft: 18 }}
              onClick={() => { setShowFinal(true); setShowQuiz(false); setActiveLesson(null); }}
            >
              <span className="pl-check">{finalPassed ? <Check size={14} strokeWidth={1.75} /> : <Circle size={14} strokeWidth={1.75} />}</span>
              <span className="pl-title" style={{ fontWeight: 600 }}>Final Assessment</span>
              <span className="pl-meta"><FileText size={14} strokeWidth={1.75} /> {finalTest.questions.length} Q</span>
            </button>
          </div>
        )}
        {hasCert && (
          <button className="btn btn-primary player-cert-btn" onClick={() => navigateTo('certificates')}>
            <Trophy size={16} strokeWidth={1.75} /> View Certificate
          </button>
        )}
        {user && <SidebarRating courseId={courseId} userId={user.id} />}
      </div>

      <div className="player-main">
        {showOverview ? (
          <div className="player-content">
            <CourseOverview
              course={course}
              progress={progress}
              quizScores={quizScores}
              assignment={assignments?.[courseId]}
              onStart={(mod, lesson) => selectLesson(mod, lesson)}
            />
          </div>
        ) : showFinal && finalTest ? (
          <div className="player-content">
            <Quiz
              userId={user.id}
              courseId={courseId}
              quiz={finalTest}
              quizScores={quizScores}
              submitQuiz={async (cId, qId, answers) => {
                const res = await submitQuiz(cId, qId, answers);
                if (res.passed) issueCertificate(cId);
                return res;
              }}
              onDone={() => navigateTo('certificates')}
              isFinal
            />
          </div>
        ) : showQuiz && activeModule?.quiz ? (
          <div className="player-content">
            <Quiz
              userId={user.id}
              courseId={courseId}
              quiz={activeModule.quiz}
              quizScores={quizScores}
              submitQuiz={submitQuiz}
              onDone={() => {
                setShowQuiz(false);
                const mods  = course.modules;
                const mIdx  = mods.findIndex(m => m.id === activeModule.id);
                if (mIdx < mods.length - 1) { const next = mods[mIdx+1]; selectLesson(next, next.lessons[0]); }
              }}
            />
          </div>
        ) : activeLesson ? (
          <div className={`player-content${activeLesson.type === 'presentation' ? ' player-content--wide' : ''}`}>
            <div className="lesson-header">
              <div>
                <p className="lesson-module-name">{activeModule?.title} · Lesson {activeModule.lessons.findIndex(l => l.id === activeLesson.id) + 1} of {activeModule.lessons.length}</p>
                <h2 className="lesson-title">{activeLesson.title}</h2>
              </div>
              <span className="lesson-type">{activeLesson.type === 'video' ? <><PlayCircle size={14} strokeWidth={1.75} /> Video</> : activeLesson.type === 'presentation' ? <><Presentation size={14} strokeWidth={1.75} /> Live Presentation</> : <><FileText size={14} strokeWidth={1.75} /> Reading</>} · {activeLesson.duration}</span>
            </div>

            <ModuleGuide module={activeModule} />

            {activeLesson.type === 'video' && (
              <div className="video-player">
                {activeLesson.videoId ? (
                  <>
                  <div className="video-embed-wrap">
                    <iframe
                      className="video-embed"
                      src={`https://www.youtube.com/embed/${activeLesson.videoId}?rel=0&modestbranding=1&cc_load_policy=1&hl=en`}
                      title={activeLesson.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  </div>
                  <div className="lesson-video-source">
                    <span>{activeLesson.videoSource || 'YouTube'}</span>
                    <a href={`https://www.youtube.com/watch?v=${activeLesson.videoId}`} target="_blank" rel="noopener noreferrer">Watch on YouTube ↗</a>
                  </div>
                  {activeLesson.transcript ? (
                    <details className="lesson-transcript">
                      <summary>Read the transcript</summary>
                      <div className="lesson-transcript-body">
                        {String(activeLesson.transcript).split(/\n{2,}/).map((para, i) => <p key={i}>{para}</p>)}
                      </div>
                    </details>
                  ) : (
                    <p className="lesson-transcript-missing">Captions are switched on in the player. A written transcript for this video has not been added yet.</p>
                  )}
                  </>
                ) : (
                  <div className="video-thumb" style={{ background: `linear-gradient(135deg, ${withAlpha(color, 13)}, ${withAlpha(color, 3)})`, borderColor: withAlpha(color, 15) }}>
                    <span className="video-label" style={{ color: 'var(--text-muted)' }}>Video coming soon</span>
                  </div>
                )}
              </div>
            )}

            {activeLesson.type === 'presentation' && (
              <PresentationPlayer user={user} presentationId={activeLesson.presentationId} />
            )}

            {activeLesson.type !== 'presentation' && activeLesson.letterhead && (
              <div className="lesson-body">
                <PreReadingLetterhead course={course} lesson={activeLesson} />
              </div>
            )}

            {activeLesson.type !== 'presentation' && !activeLesson.letterhead && (
            <div className="lesson-body">
              {activeLesson.intro && <p className="lesson-intro">{activeLesson.intro}</p>}
              {activeLesson.sections && activeLesson.sections.length > 0 ? (() => {
                const [first, ...rest] = activeLesson.sections;
                const objItems = first.content
                  ? first.content.split('\n').filter(l => l.trimStart().startsWith('- ')).map(l => l.trimStart().slice(2))
                  : [];
                return (
                  <>
                    {objItems.length > 0 && <LessonObjectives items={objItems} />}
                    {rest.map((sec, i) => (
                      <RenderSection key={sec.id || i} section={sec} color={course.color || color} ctx={{ userId: user?.id, lessonId: activeLesson.id }} />
                    ))}
                  </>
                );
              })() : renderContent(activeLesson.content)}
            </div>
            )}

            <div className="lesson-actions">
              {!progress[activeLesson.id] && (
                <button className="btn btn-primary" onClick={() => { markDone(); }}>Mark Complete <Check size={16} strokeWidth={1.75} /></button>
              )}
              {progress[activeLesson.id] && (
                <span className="lesson-done-badge"><Check size={14} strokeWidth={1.75} /> Completed</span>
              )}
              <button className="btn btn-ghost" onClick={goNext}>
                {allLessonsInModuleDone(activeModule) && activeModule.quiz && !quizScores[activeModule.quiz.id]?.passed ? 'Take Quiz →' : 'Next Lesson →'}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
